import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useApi } from "@/core/api";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import { usePackChatRequest } from "./conditional-request";
import { PACK_CHAT_STALE_TIME, packChatQueryOptions } from "./refresh";
import { selectPackChatTechnicianGroups } from "./technician-groups";
import { updateThreadSummary } from "./thread-cache";
import { isTemporaryPackChatError } from "./thread-error-copy";
import type {
  IPackChatContext,
  IPackChatReplyResponse,
  IPackChatStartOptionsResponse,
  IPackChatThreadDetail,
  IPackChatThreadDetailResponse,
  IPackChatThreadOverview,
  IPackChatThreadSummary,
  IPackChatThreadSummaryResponse,
  IPackChatThreadsResponse,
  PackChatStatus,
} from "./types";

/** Keep temporary refresh failures readable only after a fresh successful response. */
const useCurrentPackChatResult = <
  T extends Parameters<typeof currentPayrollResult>[0] & {
    error: unknown;
    dataUpdatedAt: number;
  },
>(
  query: T,
  scope: string,
): Omit<T, "data" | "isLoading"> & {
  data: T["data"] | undefined;
  isLoading: boolean;
} => {
  const baseline = useRef({ scope, dataUpdatedAt: query.dataUpdatedAt });
  if (baseline.current.scope !== scope) {
    baseline.current = { scope, dataUpdatedAt: query.dataUpdatedAt };
  }
  useEffect(() => {
    if (query.isError && !isTemporaryPackChatError(query.error)) {
      baseline.current.dataUpdatedAt = query.dataUpdatedAt;
    }
  }, [query.isError, query.error, query.dataUpdatedAt]);
  const current = currentPayrollResult(query);
  return {
    ...current,
    data:
      query.dataUpdatedAt > baseline.current.dataUpdatedAt &&
      isTemporaryPackChatError(query.error)
        ? query.data
        : current.data,
  };
};

const MANAGED_PACK_THREADS_KEY = "managed-pack-threads";

export const managedPackThreadsKey = (
  businessId: string | null,
  periodStart: string | null,
) => [MANAGED_PACK_THREADS_KEY, businessId, periodStart] as const;

export const managedPackThreadKey = (
  businessId: string | null,
  periodStart: string | null,
  threadId: string | null,
) =>
  [
    MANAGED_PACK_THREADS_KEY,
    businessId,
    periodStart,
    "detail",
    threadId,
  ] as const;

export const managedPackStartOptionsKey = (
  businessId: string | null,
  periodStart: string | null,
) =>
  [MANAGED_PACK_THREADS_KEY, businessId, periodStart, "start-options"] as const;

const path = (businessId: string, periodStart: string) =>
  `/businesses/${businessId}/pay_periods/${periodStart}/statement_threads`;

export const invalidateManagedPackThreadQueries = (
  queryClient: QueryClient,
  businessId: string | null,
  periodStart: string | null,
  threadId?: string,
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: managedPackThreadsKey(businessId, periodStart),
      exact: true,
    }),
    ...(threadId
      ? [
          queryClient.invalidateQueries({
            queryKey: managedPackThreadKey(businessId, periodStart, threadId),
            exact: true,
          }),
        ]
      : []),
  ]);

export const updateManagedPackThreadListCache = (
  queryClient: QueryClient,
  businessId: string | null,
  periodStart: string | null,
  thread: IPackChatThreadSummary,
) => {
  queryClient.setQueryData<IPackChatThreadsResponse>(
    managedPackThreadsKey(businessId, periodStart),
    (current) => updateThreadSummary(current, thread),
  );
};

const updateDetail = (
  queryClient: QueryClient,
  businessId: string | null,
  periodStart: string | null,
  threadId: string,
  update: (thread: IPackChatThreadDetail) => IPackChatThreadDetail,
) => {
  queryClient.setQueryData<IPackChatThreadDetailResponse>(
    managedPackThreadKey(businessId, periodStart, threadId),
    (current) => (current ? { thread: update(current.thread) } : current),
  );
};

/**
 * The period's threads, unfiltered.
 *
 * The endpoint returns the complete period. The tab needs all three counts at
 * once, so it filters in `select`. One cache entry serves both the tab badge and
 * the queue.
 */
export const useGetManagedPackThreads = <TData = IPackChatThreadsResponse>(
  businessId: string | null,
  periodStart: string | null,
  options?: {
    enabled?: boolean;
    select?: (data: IPackChatThreadsResponse) => TData;
  },
) => {
  const request = usePackChatRequest(businessId);

  const query = useQuery({
    queryKey: managedPackThreadsKey(businessId, periodStart),
    queryFn: ({ signal }) => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");

      return request<IPackChatThreadsResponse>(
        path(businessId, periodStart),
        managedPackThreadsKey(businessId, periodStart),
        signal,
      );
    },
    select: options?.select,
    enabled: (options?.enabled ?? true) && !!businessId && !!periodStart,
    ...packChatQueryOptions,
    // Each manager view requires a fresh access check before showing cached data.
    refetchOnMount: "always",
  });
  return useCurrentPackChatResult(
    query,
    JSON.stringify(managedPackThreadsKey(businessId, periodStart)),
  );
};

/**
 * The list, shaped the way the PackChat tab reads it.
 *
 * Filters and search live in the component; turning a flat array of threads into
 * per-technician groups does not, so it rides on the query's `select` — the same
 * pattern as the user query selectors. react-query memoises it
 * against the cached response, and the unfiltered fetch is shared with the tab
 * badge rather than duplicated per filter.
 */
export const useGetManagedPackThreadGroups = (
  businessId: string | null,
  periodStart: string | null,
  filters?: { status?: PackChatStatus; search?: string },
) =>
  useGetManagedPackThreads<IPackChatThreadOverview>(businessId, periodStart, {
    select: useMemo(
      () =>
        selectPackChatTechnicianGroups({
          status: filters?.status,
          search: filters?.search,
        }),
      [filters?.status, filters?.search],
    ),
  });

export const useGetManagedPackThread = (
  businessId: string | null,
  periodStart: string | null,
  threadId: string | null,
) => {
  const request = usePackChatRequest(businessId);

  const query = useQuery({
    queryKey: managedPackThreadKey(businessId, periodStart, threadId),
    queryFn: ({ signal }) => {
      if (!businessId || !periodStart || !threadId) {
        throw new Error("Thread is required");
      }
      return request<IPackChatThreadDetailResponse>(
        `${path(businessId, periodStart)}/${threadId}`,
        managedPackThreadKey(businessId, periodStart, threadId),
        signal,
      );
    },
    enabled: !!businessId && !!periodStart && !!threadId,
    ...packChatQueryOptions,
    refetchOnMount: "always",
  });
  return useCurrentPackChatResult(
    query,
    JSON.stringify(managedPackThreadKey(businessId, periodStart, threadId)),
  );
};

export const useGetManagedPackStartOptions = (
  businessId: string | null,
  periodStart: string | null,
  enabled = true,
) => {
  const api = useApi();

  const query = useQuery({
    queryKey: managedPackStartOptionsKey(businessId, periodStart),
    queryFn: () => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");
      return api
        .get(`${path(businessId, periodStart)}/start_options`)
        .then((res) => res.data as IPackChatStartOptionsResponse);
    },
    enabled: enabled && !!businessId && !!periodStart,
    retry: false,
    staleTime: PACK_CHAT_STALE_TIME,
    refetchOnMount: "always",
  });
  return currentPayrollResult(query);
};

export const useStartManagedPackThread = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      technicianId: string;
      runId: string;
      body: string;
      context: IPackChatContext;
    }) => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");
      return api
        .post(path(businessId, periodStart), {
          technician_id: payload.technicianId,
          ai_script_run_id: payload.runId,
          body: payload.body,
          context: payload.context,
        })
        .then((res) => res.data as IPackChatThreadDetailResponse);
    },
    onSuccess: async ({ thread }) => {
      await queryClient.cancelQueries({
        queryKey: managedPackThreadsKey(businessId, periodStart),
      });
      queryClient.setQueryData(
        managedPackThreadKey(businessId, periodStart, thread.id),
        { thread },
      );
      return invalidateManagedPackThreadQueries(
        queryClient,
        businessId,
        periodStart,
      );
    },
  });
};

export const useReplyToManagedPackThread = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ threadId, body }: { threadId: string; body: string }) => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");
      return api
        .post(`${path(businessId, periodStart)}/${threadId}/messages`, {
          body,
        })
        .then((res) => res.data as IPackChatReplyResponse);
    },
    onSuccess: async ({ thread, message }) => {
      await queryClient.cancelQueries({
        queryKey: managedPackThreadsKey(businessId, periodStart),
      });
      updateManagedPackThreadListCache(
        queryClient,
        businessId,
        periodStart,
        thread,
      );
      updateDetail(
        queryClient,
        businessId,
        periodStart,
        thread.id,
        (current) => ({
          ...current,
          ...thread,
          status: "open",
          messages: current.messages.some((item) => item.id === message.id)
            ? current.messages
            : [...current.messages, message],
        }),
      );
      return invalidateManagedPackThreadQueries(
        queryClient,
        businessId,
        periodStart,
        thread.id,
      );
    },
  });
};

export const useCloseManagedPackThread = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (threadId: string) => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");
      return api
        .patch(`${path(businessId, periodStart)}/${threadId}/close`)
        .then((res) => res.data as IPackChatThreadSummaryResponse);
    },
    onSuccess: async ({ thread }) => {
      await queryClient.cancelQueries({
        queryKey: managedPackThreadsKey(businessId, periodStart),
      });
      updateManagedPackThreadListCache(
        queryClient,
        businessId,
        periodStart,
        thread,
      );
      updateDetail(
        queryClient,
        businessId,
        periodStart,
        thread.id,
        (current) => ({ ...current, ...thread, messages: current.messages }),
      );
      return invalidateManagedPackThreadQueries(
        queryClient,
        businessId,
        periodStart,
        thread.id,
      );
    },
  });
};
